/**
 * @author Maxime Mustarda <maxime@inarix.com>
 * @coauthor David Roman <david@inarix.com>
 * @file LicencesTable.tsx
 * @desc Created on Mon Apr 28 2025 20:44:52
 * @copyright All rights reserved @ Inarix
 */

import { FC, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Modal,
  Snackbar,
  ThemeProvider,
} from "@mui/material";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { TFunction } from "i18next";

import themeInarix from "../StylesMuiInarix";
import { selectLocale } from "../../redux/slices/layout";
import {
  focusedLicense,
  licenseAssignation,
  clearLicenseNotification,
  selectLicenseLoading,
  selectLicenseNotification,
  selectModalLicense,
  setLicenseId,
  LicensesFilters,
  setShowFilterForm,
  setPreselectedFilterField,
  selectUnassignedCount,
  selectExpiredCount,
  selectTotalLicensesCount,
  setFilterModel,
  setOpenCells,
  selectUpcomingCount,
  selectUsersWithNoLicenses,
} from "../../redux/slices/licenses/licenseList";
import { LicensePacksModal } from "./LicensePacksModal";
import { useLicenseColumns } from "./LicenceColumns";
import LicenseAssignationModal from "./LicenseAssignationModal";
import AssignLicenseToUserModal, {
  UserToAssign,
} from "./AssignLicenseToUserModal";
import {
  getDataGridLocale,
  managingFiltersText,
} from "../data/dataHelpers/DataProgress";
import CustomGeneralColumnHeader from "../customFilterComponent/customHeader";
import { GroupCell } from "./GroupCell";
import { LicenseQuickFilter } from "./LicenseQuickFilter";
import { FirstLetterUpperCase } from "../helpers/helpersSearchs";
import { LicenseStylesColums } from "./licenseStyles/LicenseColumns";
import { useLicenseTableOptions } from "./useLicenseTableOptions";
import {
  tableContainerSx,
  tableDescriptionStyle,
  alertSx,
  bulkBarSx,
  bulkBarLabelStyle,
  bulkBarButtonSx,
} from "./licenseStyles/LicencesTable.styles";

type LicencesTableParams = {
  apiRef: ReturnType<typeof useGridApiRef>;
  initData: (dispatch: ThunkDispatch<any, any, any>) => void;
  t: TFunction<"global", undefined>;
};

const SetActions = (dispatch: ThunkDispatch<any, any, any>) => {
  return {
    setLicenseId: (id: string) => dispatch(setLicenseId(id)),
    focusedLicense: (id: string) => dispatch(focusedLicense(id)),
    setOpenCells: (open: boolean) => dispatch(setOpenCells(open)),
    onOpenFilter: (field: string) => {
      dispatch(setShowFilterForm(false));
      dispatch(setPreselectedFilterField(field));
      setTimeout(() => {
        dispatch(setShowFilterForm(true));
      }, 50);
    },
  };
};

export const LicencesTable: FC<LicencesTableParams> = ({
  apiRef,
  initData,
  t,
}) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const locale = useSelector(selectLocale);
  const loading = useSelector(selectLicenseLoading);
  const modalLicense = useSelector(selectModalLicense);
  const { licenseId } = useSelector(licenseAssignation);
  const notification = useSelector(selectLicenseNotification);
  const { filterModel } = useSelector(LicensesFilters);
  const unassignedCount = useSelector(selectUnassignedCount) as number;
  const expiredCount = useSelector(selectExpiredCount) as number;
  const upcomingCount = useSelector(selectUpcomingCount) as number;
  const totalCount = useSelector(selectTotalLicensesCount) as number;
  const usersWithNoLicense = useSelector(selectUsersWithNoLicenses) as any[];
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [
    selectedUserLicenseAlmostExpired,
    setSelectedUserLicenseAlmostExpired,
  ] = useState<string[]>([]);

  const [assignToUserData, setAssignToUserData] = useState<
    UserToAssign[] | null
  >(null);
  const { tableOptions, expandedRows, setExpandedRows } =
    useLicenseTableOptions({ apiRef, t });
  const {
    licenseColumns,
    licensesRows,
    sortAlpha,
    toggleAlphaSort,
    activeSort,
  } = useLicenseColumns(t, dispatch, tableOptions, activeFilter);
  const stableRows = useMemo(() => licensesRows, [licensesRows]);
  const { setLicenseId, focusedLicense, onOpenFilter } = useMemo(
    () => SetActions(dispatch),
    [dispatch],
  );
  const [loadingAssignLicense, setLoadingAssignLicense] = useState(false);

  const isUsersNoLicensesMode = activeFilter === "usersNoLicenses";
  const isLicensesExpiringSoonMode = filterModel.items.find(
    (item) => item.field === "licensesStatus" && item.value === "expiringSoon",
  )
    ? true
    : false;

  const activeCount = Math.max(
    totalCount - unassignedCount - expiredCount - upcomingCount,
    0,
  );

  const filterOptions = [
    {
      key: "all",
      label: FirstLetterUpperCase(t("licenses.all")),
      count: totalCount,
      color: LicenseStylesColums.statusColors.all.color,
      bg: LicenseStylesColums.statusColors.all.bg,
    },
    {
      key: "active",
      label: FirstLetterUpperCase(t("licenses.active")),
      count: activeCount > 0 ? activeCount : 0,
      color: LicenseStylesColums.statusColors.active.color,
      bg: LicenseStylesColums.statusColors.active.bg,
    },

    {
      key: "upcoming",
      label: FirstLetterUpperCase(t("licenses.upcoming")),
      count: upcomingCount,
      color: LicenseStylesColums.statusColors.upcoming.color,
      bg: LicenseStylesColums.statusColors.upcoming.bg,
    },
    {
      key: "expired",
      label: FirstLetterUpperCase(t("licenses.expired")),
      count: expiredCount,
      color: LicenseStylesColums.statusColors.expired.color,
      bg: LicenseStylesColums.statusColors.expired.bg,
    },
    {
      key: "usersNoLicenses",
      label: FirstLetterUpperCase(t("licenses.users_with_no_licenses")),
      count: usersWithNoLicense?.length,
      color: LicenseStylesColums.statusColors.noLicense.color,
      bg: LicenseStylesColums.statusColors.noLicense.bg,
    },
    {
      key: "unassigned",
      label: t("licenses.unassigned"),
      count: unassignedCount,
      color: LicenseStylesColums.statusColors.unassigned.color,
      bg: LicenseStylesColums.statusColors.unassigned.bg,
    },
  ];

  const optionsQuickFilter = () => (
    <LicenseQuickFilter
      filterOptions={filterOptions}
      activeFilter={activeFilter}
      onFilterChange={(key) => {
        setActiveFilter(key);
        setSelectedUserIds([]);
      }}
      filterModel={filterModel}
      setFilterModels={(model) => dispatch(setFilterModel(model))}
      t={t}
    />
  );

  const handleAssignToUser = useCallback(
    (userId: string) => {
      const user = usersWithNoLicense.find((u: any) => u.id === userId);
      if (!user) return;
      const userGroupId =
        user.groups?.find((g: any) => g.typeId === 107)?.id ?? null;
      if (!userGroupId) return;
      setAssignToUserData([{ userId, userGroupId, username: user.username }]);
    },
    [usersWithNoLicense],
  );

  const onToggleRow = useCallback(
    (id: string, expanded: boolean) => {
      setExpandedRows((prev) => {
        const next = new Set(prev);
        expanded ? next.add(id) : next.delete(id);
        return next;
      });
    },
    [setExpandedRows],
  );

  const groupingColDef = useMemo(
    () => ({
      headerName: t("Data.table.Username"),
      width: 260,
      renderHeader() {
        return (
          <CustomGeneralColumnHeader
            headerName={t("Data.table.Username")}
            field={"username"}
            filterModel={filterModel}
            availableFilters={["$ilike", "$eq", "$ne"]}
            onOpenFilter={() => onOpenFilter("username")}
            backgroundColor="#fafafa"
            newOptions={tableOptions}
            sortable={true}
            sortModel={
              activeSort === "username" && sortAlpha
                ? [{ field: "username", sort: sortAlpha }]
                : []
            }
            onSort={toggleAlphaSort}
          />
        );
      },
      renderCell: (params: any) => (
        <GroupCell
          params={params}
          t={t}
          onAssign={setLicenseId}
          onAssignToUser={handleAssignToUser}
          onToggle={onToggleRow}
        />
      ),
    }),
    [
      t,
      filterModel,
      onOpenFilter,
      tableOptions,
      activeSort,
      sortAlpha,
      toggleAlphaSort,
      setLicenseId,
      handleAssignToUser,
      onToggleRow,
    ],
  );

  const handleAssignToUsers = (userIds: string[]) => {
    const mapped = userIds.flatMap((id) => {
      const user = usersWithNoLicense.find((u) => u.id === id);
      if (!user) return [];
      const userGroupId =
        user.groups?.find((g: any) => g.typeId === 107)?.id ?? null;
      if (!userGroupId) return [];
      return [{ userId: id, userGroupId, username: user.username }];
    });
    if (mapped.length > 0) setAssignToUserData(mapped);
  };

  const bulkBar = isUsersNoLicensesMode && selectedUserIds.length > 0 && (
    <Box sx={bulkBarSx}>
      <span style={bulkBarLabelStyle}>
        {selectedUserIds.length} {t("licenses.users_selected")}
      </span>
      <Button
        variant="contained"
        size="small"
        sx={bulkBarButtonSx}
        onClick={() => handleAssignToUsers(selectedUserIds)}
        disabled={selectedUserIds.length > unassignedCount}
      >
        {selectedUserIds.length === 1
          ? `${t("licenses.assignLicense")}`
          : `${t("licenses.assignLicense_other")}`}
      </Button>
      <Button
        variant="outlined"
        size="small"
        sx={bulkBarButtonSx}
        onClick={() => setSelectedUserIds([])}
      >
        {t("Common_options.clear_selection")}
      </Button>
    </Box>
  );

  const bulkBarExpiringSoon =
    isLicensesExpiringSoonMode &&
    selectedUserLicenseAlmostExpired.length > 0 ? (
      <Box sx={bulkBarSx}>
        <span style={bulkBarLabelStyle}>
          {selectedUserLicenseAlmostExpired.length}{" "}
          {t("licenses.licenses_selected")}
        </span>
        <Button
          variant="contained"
          size="small"
          sx={bulkBarButtonSx}
          onClick={() => {
            const usersToAssign = selectedUserLicenseAlmostExpired.flatMap(
              (parentId) => {
                const username = String(parentId).split("/").pop() ?? "";
                const row = stableRows.find((r) => r.username === username);
                if (!row?.userId || !row?.license?.ownerGroupId) return [];
                return [
                  {
                    userId: row.userId,
                    userGroupId: row.license.ownerGroupId,
                    username,
                  },
                ];
              },
            );
            if (usersToAssign.length > 0) setAssignToUserData(usersToAssign);
          }}
        >
          {t("licenses.renewal")}
        </Button>
        <Button
          variant="outlined"
          size="small"
          sx={bulkBarButtonSx}
          onClick={() => setSelectedUserLicenseAlmostExpired([])}
        >
          {t("Common_options.clear_selection")}
        </Button>
      </Box>
    ) : null;

  return (
    <Box>
      <ThemeProvider theme={themeInarix(locale)}>
        <div className="container-licenses-table">
          <Box sx={tableContainerSx}>
            <div style={tableDescriptionStyle}>
              <Alert severity="info" sx={alertSx}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: !isUsersNoLicensesMode
                      ? t("licenses.tableDescription")
                      : unassignedCount > 0
                        ? t(
                            "licenses.tableDescriptionUsersNoLicence",
                          ).replaceAll("{n}", String(unassignedCount))
                        : t("licenses.tableDescriptionUsersNoLicenceEmpty"),
                  }}
                />
              </Alert>
            </div>

            {optionsQuickFilter()}
            {bulkBar}
            {bulkBarExpiringSoon}
            <DataGridPro
              apiRef={apiRef}
              rows={stableRows}
              columns={licenseColumns}
              rowHeight={70}
              pagination
              checkboxSelection={
                isUsersNoLicensesMode || isLicensesExpiringSoonMode
              }
              disableRowSelectionOnClick={!isUsersNoLicensesMode}
              rowSelectionModel={
                isUsersNoLicensesMode
                  ? selectedUserIds.map((id) => `${id}`)
                  : isLicensesExpiringSoonMode
                    ? selectedUserLicenseAlmostExpired.map((id) => `${id}`)
                    : []
              }
              onRowSelectionModelChange={
                isUsersNoLicensesMode
                  ? (model) =>
                      setSelectedUserIds(
                        (model as string[])
                          .map((id) => String(id))
                          .slice(0, unassignedCount),
                      )
                  : undefined
              }
              onCellClick={
                isLicensesExpiringSoonMode
                  ? (params, event) => {
                      if (params.field !== "__check__") return;
                      if (params.row.id) return;
                      event.defaultMuiPrevented = true;
                      const rowId = String(params.id);
                      setSelectedUserLicenseAlmostExpired((prev) =>
                        prev.includes(rowId)
                          ? prev.filter((id) => id !== rowId)
                          : [...prev, rowId],
                      );
                    }
                  : undefined
              }
              isRowSelectable={(params) =>
                isLicensesExpiringSoonMode
                  ? !params.row.id
                  : unassignedCount > 0
              }
              getRowClassName={(params) =>
                isLicensesExpiringSoonMode && !!params.row.id
                  ? "license-row--no-checkbox"
                  : ""
              }
              sx={{
                "& .license-row--no-checkbox .MuiDataGrid-cellCheckbox": {
                  visibility: "hidden",
                  pointerEvents: "none",
                },
              }}
              disableColumnSelector={true}
              treeData
              getTreeDataPath={(row) => row.path}
              defaultGroupingExpansionDepth={0}
              density="compact"
              isGroupExpandedByDefault={(node) =>
                expandedRows.has(String(node.id))
              }
              groupingColDef={groupingColDef}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25 },
                },
              }}
              loading={loading}
              pageSizeOptions={[25, 50, 100]}
              ignoreDiacritics={true}
              localeText={
                {
                  ...getDataGridLocale(locale),
                  noRowsLabel: managingFiltersText(
                    filterModel,
                    t,
                    licensesRows,
                  ),
                } as any
              }
            />
          </Box>
        </div>
        <Modal open={!!modalLicense} onClose={() => focusedLicense("")}>
          <LicensePacksModal />
        </Modal>

        <Modal
          open={!!licenseId}
          onClose={loadingAssignLicense ? undefined : () => setLicenseId("")}
        >
          <LicenseAssignationModal
            initData={initData}
            t={t}
            setLoadingAssignLicense={setLoadingAssignLicense}
            loadingAssignLicense={loadingAssignLicense}
          />
        </Modal>

        <Modal
          open={!!assignToUserData}
          onClose={() => setAssignToUserData(null)}
        >
          <Box>
            {assignToUserData && (
              <AssignLicenseToUserModal
                users={assignToUserData}
                initData={initData}
                t={t}
                onClose={() => setAssignToUserData(null)}
              />
            )}
          </Box>
        </Modal>

        <Snackbar
          open={notification.open}
          autoHideDuration={4000}
          onClose={() => dispatch(clearLicenseNotification())}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => dispatch(clearLicenseNotification())}
            severity={notification.severity}
          >
            {notification.message ? t(`licenses.${notification.message}`) : ""}
          </Alert>
        </Snackbar>
      </ThemeProvider>
    </Box>
  );
};
